import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

/** i18n keys used by CB2 mocks and by the webapp when CB text inputs are empty. */
export const KIOSK_PAYMENT_METHOD_PICKER_FALLBACK_I18N_KEYS = {
  headingChoosePaymentMethod: 'kiosk-payment-method-picker.choice_heading',
  choicePayOnSite: 'kiosk-payment-method-picker.choice_pay_on_site',
  choicePayOnKiosk: 'kiosk-payment-method-picker.choice_pay_on_kiosk',
  terminalInstructionMessage: 'kiosk-payment-method-picker.terminal_instruction_message',
  payOnKioskText: 'kiosk-payment-method-picker.pay_on_kiosk_text',
} as const;

export type KioskPaymentMethodPickerFallbackI18nKey =
  (typeof KIOSK_PAYMENT_METHOD_PICKER_FALLBACK_I18N_KEYS)[keyof typeof KIOSK_PAYMENT_METHOD_PICKER_FALLBACK_I18N_KEYS];

export function resolveKioskPaymentMethodPickerText(
  fallbackKey: KioskPaymentMethodPickerFallbackI18nKey,
  ...values: readonly unknown[]
): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return fallbackKey;
}

/**
 * The preview scenario stores a non-empty i18n key as a placeholder.
 * The value typed on the block must win, otherwise the webapp preview
 * keeps the placeholder and ignores the inspector.
 */
export function resolveKioskPaymentMethodPickerDisplayedText(
  fallbackKey: KioskPaymentMethodPickerFallbackI18nKey,
  configured: unknown,
  preview: unknown,
): string {
  return resolveKioskPaymentMethodPickerText(fallbackKey, configured, preview);
}

export type KioskPaymentMethodPickerBlockScenarioContent = {
  headingChoosePaymentMethod: string;
  choicePayOnSite: string;
  choicePayOnKiosk: string;
  terminalInstructionMessage: string;
  payOnKioskText: string;
  displayPayOnKioskImage: boolean;
  payOnKioskImage: {
    url: string;
    width?: number;
    height?: number;
  };
  order: {
    final_price: number;
    [key: string]: unknown;
  } | null;
  availablePaymentMethodHandles: readonly string[];
  step: 'CHOICE' | 'TERMINAL';
};
export const KIOSKPAYMENTMETHODPICKERBLOCK_LEGACY_FIELD_ORDER = [
  { type: 'heading', value: 'Ecran "choix"' },
  'heading_choose_payment_method',
  'choice_pay_on_site',
  'choice_pay_on_kiosk',
  { type: 'heading', value: 'Ecran "Paiement sur la borne"' },
  'terminal_instruction_message',
  'pay_on_kiosk_text',
  { type: 'heading', value: 'Bouton Annuler' },
  'display_cancel_button',
  'cancel_button_label',
  'cancel_button_background_color',
  'cancel_button_text_color',
  { type: 'heading', value: 'Couleurs' },
  'heading_color',
  'card_inactive_background_color',
  'card_inactive_text_color',
  'card_active_background_color',
  'card_active_text_color',
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskPaymentMethodPickerBlockDescriptor: _generatedKioskPaymentMethodPickerBlockDescriptor,
  kioskPaymentMethodPickerBlockBlocDefinition,
  kioskPaymentMethodPickerBlockDashboardLayout,
  kioskPaymentMethodPickerBlockConfig,
  kioskPaymentMethodPickerBlockResolveConfig,
  kioskPaymentMethodPickerBlockProperties,
  kioskPaymentMethodPickerBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: 'Kiosk payment method picker',
    componentType: 'KioskPaymentMethodPickerBlockComponent',
    category: 'content',
    description: 'Kiosk payment method picker.',
    keywords: [],
    allowedIn: [{ bloc: 'PaymentPageComponent', slot: 'body' }],
    sharedFields: {},
    slots: [
      {
        key: 'header',
        name: 'pages.blocks.kiosk-payment-method-picker.header.label',
        accept: 'explicitOnly',
        allowedTypes: ['KioskHeaderBlock'],
        slotItemFormat: 'ids',
        defaultBlocks: [{ id: 'kiosk-header-block', type: 'KioskHeaderBlockComponent' }],
      },
      {
        key: 'terminal_content',
        name: 'pages.blocks.kiosk-payment-method-picker.terminal-content.label',
        accept: 'explicitOnly',
        allowedTypes: [
          'TextBlock',
          'FrameBlock',
          'ImageFullWidthBlock',
          'ImageVariableWidthBlock',
          'ImageAndTextBlock',
        ],
        slotItemFormat: 'ids',
      },
    ],
  },
  () => ({
    heading_choose_payment_method: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.heading-choose-payment-method.label',
        placeholder:
          'pages.blocks.kiosk-payment-method-picker.heading-choose-payment-method.placeholder',
      },
    }),
    choice_pay_on_site: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.choice-pay-on-site.label',
        placeholder: 'pages.blocks.kiosk-payment-method-picker.choice-pay-on-site.placeholder',
      },
    }),
    choice_pay_on_kiosk: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.choice-pay-on-kiosk.label',
        placeholder: 'pages.blocks.kiosk-payment-method-picker.choice-pay-on-kiosk.placeholder',
      },
    }),
    terminal_instruction_message: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.terminal-instruction-message.label',
        placeholder:
          'pages.blocks.kiosk-payment-method-picker.terminal-instruction-message.placeholder',
      },
    }),
    pay_on_kiosk_text: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.pay-on-kiosk-text.label',
        placeholder: 'pages.blocks.kiosk-payment-method-picker.pay-on-kiosk-text.placeholder',
      },
    }),
    display_pay_on_kiosk_image: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.display-pay-on-kiosk-image.label',
      },
    }),
    pay_on_kiosk_image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.pay-on-kiosk-image.label',
        help: 'pages.blocks.kiosk-payment-method-picker.pay-on-kiosk-image.help',
      },
    }),
    heading_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'primary-800' },
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.heading-color.label',
        help: 'pages.blocks.kiosk-payment-method-picker.heading-color.help',
      },
    }),
    card_inactive_background_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: 'transparent' },
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.card-inactive-background-color.label',
      },
    }),
    card_inactive_text_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'primary-800' },
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.card-inactive-text-color.label',
      },
    }),
    card_active_background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'primary-800' },
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.card-active-background-color.label',
      },
    }),
    card_active_text_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#FFFFFF' },
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.card-active-text-color.label',
      },
    }),
    display_cancel_button: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.display-cancel-button.label',
      },
    }),
    cancel_button_label: propertyValue({
      type: 'translation',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.cancel-button-label.label',
        help: 'pages.blocks.kiosk-payment-method-picker.cancel-button-label.help',
      },
    }),
    cancel_button_background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-300' },
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.cancel-button-background-color.label',
      },
    }),
    cancel_button_text_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-900' },
      options: {
        label: 'pages.blocks.kiosk-payment-method-picker.cancel-button-text-color.label',
      },
    }),
  }),
  () => ({
    omitFields: ['display_pay_on_kiosk_image', 'pay_on_kiosk_image'] as const,
    tabs: [
      {
        label: 'pages.blocks.kiosk-payment-method-picker.tab.content.label',
        fieldsets: [
          {
            legend: 'pages.blocks.kiosk-payment-method-picker.fieldset.header.label',
            fields: ['header'],
          },
          {
            legend: 'pages.blocks.kiosk-payment-method-picker.fieldset.choice-screen.label',
            fields: [
              'heading_choose_payment_method',
              'heading_color',
              'choice_pay_on_site',
              'choice_pay_on_kiosk',
            ],
          },
          {
            legend: 'pages.blocks.kiosk-payment-method-picker.fieldset.choice-cards.label',
            fields: [
              'card_inactive_background_color',
              'card_inactive_text_color',
              'card_active_background_color',
              'card_active_text_color',
            ],
          },
          {
            legend: 'pages.blocks.kiosk-payment-method-picker.fieldset.terminal-screen.label',
            fields: ['terminal_content', 'terminal_instruction_message', 'pay_on_kiosk_text'],
          },
          {
            legend: 'pages.blocks.kiosk-payment-method-picker.fieldset.cancel-button.label',
            fields: [
              'display_cancel_button',
              {
                prop: 'cancel_button_label',
                visibleWhen: { display_cancel_button: true },
              },
              {
                prop: 'cancel_button_background_color',
                visibleWhen: { display_cancel_button: true },
              },
              {
                prop: 'cancel_button_text_color',
                visibleWhen: { display_cancel_button: true },
              },
            ],
          },
        ],
      },
    ],
  }),
);

export function kioskPaymentMethodPickerBlockDescriptor(): IBlockDescriptor;
export function kioskPaymentMethodPickerBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskPaymentMethodPickerBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskPaymentMethodPickerBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedKioskPaymentMethodPickerBlockDescriptor,
    kioskPaymentMethodPickerBlockProperties,
    fieldOrder,
  );
}

export {
  kioskPaymentMethodPickerBlockBlocDefinition,
  kioskPaymentMethodPickerBlockDashboardLayout,
  kioskPaymentMethodPickerBlockProperties,
  kioskPaymentMethodPickerBlockConfig,
  kioskPaymentMethodPickerBlockResolveConfig,
  kioskPaymentMethodPickerBlockDefinitionSetupScenarios,
};

export type KioskPaymentMethodPickerBlockConfig = typeof kioskPaymentMethodPickerBlockConfig;
